import { Honeybadger } from '@honeybadger-io/react';
import axios from 'axios';
import queryString from 'query-string';
import { wlsApiRoot, wlsApiHeaders } from './wls-api';
import { ensureAbsoluteImageUrl } from '../../shared/imageUrlHelper';
import normalizeWlsBookingResponse from './wls-booking-response';
import buildWlsSearchParams from './wls-search-query';
import mapWlsSearchProperty from './map-wls-search-property';

/**
 * WLS API client — calls the dedicated WLS API Gateway via same-origin proxy.
 * Handles all guest-facing flows: search, listing details, checkout, receipt,
 * payment, verification, signatures, id-photos, lynnbrook, calendar, etc.
 */

// ---------------------------------------------------------------------------
// Search helpers
// ---------------------------------------------------------------------------

const hasV2SearchShape = data =>
  data && Array.isArray(data.results);

const featuredImageForGrid = url => {
  if (!url || typeof url !== 'string') return null;
  const absoluteUrl = ensureAbsoluteImageUrl(url);
  return { image: { url: absoluteUrl, small: { url: absoluteUrl } } };
};

const adaptGuestBookingResponse = (response, brand) => ({
  ...response,
  data: normalizeWlsBookingResponse(response.data, brand)
});

const mapPropertyToSearchResult = property =>
  mapWlsSearchProperty(property, featuredImageForGrid);

const buildSearchQuery = (searchState, brand) =>
  `?${queryString.stringify(buildWlsSearchParams(searchState, brand))}`;

const adaptSearchResponse = data => {
  if (hasV2SearchShape(data)) return data;
  if (data && Array.isArray(data.properties)) {
    const pageSize = 25;
    const total = data.total_count || data.properties.length;
    return {
      results: data.properties.map(mapPropertyToSearchResult),
      total_pages: Math.max(1, Math.ceil(total / pageSize)),
      total_properties: total,
      bounds: data.bounds || null,
      center: data.center || null,
      max_price: data.max_price,
      min_price: data.min_price ?? 0,
      max_baths: data.max_baths,
      max_bedrooms: data.max_bedrooms,
      max_guests: data.max_guests
    };
  }
  return null;
};

export const fetchWlsSearchListings = (listingType, searchState, brand) => {
  const resource = listingType === 'properties' ? 'properties' : 'vehicles';
  const url = `${wlsApiRoot(brand.organization.id)}/${resource}${buildSearchQuery(searchState, brand)}`;
  return axios.get(url, { headers: wlsApiHeaders() }).then(response => {
    const adapted = adaptSearchResponse(response.data);
    if (!adapted) {
      throw new Error('Unexpected search response shape');
    }
    return { ...response, data: adapted };
  });
};

// ---------------------------------------------------------------------------
// Booking lookup / receipt / payment / verification
// ---------------------------------------------------------------------------

export const fetchWlsBookingLookup = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/booking`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  );

export const fetchWlsReceipt = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/receipt`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  ).then(response => adaptGuestBookingResponse(response, brand));

export const fetchWlsReceiptPdf = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/receipt/pdf`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  );

export const fetchWlsArrivalReadiness = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/arrival-readiness`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  );

export const fetchWlsPayment = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/payment`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  ).then(response => adaptGuestBookingResponse(response, brand));

export const fetchWlsVerification = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/verification`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  ).then(response => adaptGuestBookingResponse(response, brand));

export const postWlsVerify = (brand, customerId, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/verify`,
    { customer_id: customerId, ...data },
    { headers: wlsApiHeaders() }
  );

// ---------------------------------------------------------------------------
// Checkout
// ---------------------------------------------------------------------------

export const postWlsCheckout = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/checkout`,
    data,
    { headers: wlsApiHeaders() }
  );

export const fetchWlsCheckoutPage = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/checkout`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

export const fetchWlsCheckoutAvailability = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/checkout/availability`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

export const fetchWlsCheckoutPricing = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/checkout/pricing`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

// ---------------------------------------------------------------------------
// Payment processing
// ---------------------------------------------------------------------------

export const postWlsProcessPayment = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/process-payment`,
    data,
    { headers: wlsApiHeaders() }
  );

export const postWlsProcessSecurityDeposit = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/process-security-deposit`,
    data,
    { headers: wlsApiHeaders() }
  );

export const postWlsAddCard = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/add-card`,
    data,
    { headers: wlsApiHeaders() }
  );

// ---------------------------------------------------------------------------
// Lynnbrook
// ---------------------------------------------------------------------------

export const fetchWlsLynnbrookConfig = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/lynnbrook-config`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

export const fetchWlsTargetId = (brand, listingId) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/target-id`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId } }
  );

// ---------------------------------------------------------------------------
// Signatures
// ---------------------------------------------------------------------------

export const postWlsSignature = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/signatures`,
    data,
    { headers: wlsApiHeaders() }
  );

export const deleteWlsSignature = (brand, signatureId, bookingCode) =>
  axios.delete(
    `${wlsApiRoot(brand.organization.id)}/signatures`,
    { headers: wlsApiHeaders(), params: { signature_id: signatureId, booking_code: bookingCode } }
  );

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const fetchWlsContract = (brand, bookingCode) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/contract`,
    { headers: wlsApiHeaders(), params: { booking_code: bookingCode } }
  );

export const postWlsContractSign = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/contract/sign`,
    data,
    { headers: wlsApiHeaders() }
  );

// ---------------------------------------------------------------------------
// ID Photos
// ---------------------------------------------------------------------------

export const postWlsIdPhoto = (brand, data) => {
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  const headers = isFormData ? {} : wlsApiHeaders();
  return axios.post(
    `${wlsApiRoot(brand.organization.id)}/id-photos`,
    data,
    { headers }
  );
};

export const deleteWlsIdPhoto = (brand, photoId, bookingCode) =>
  axios.delete(
    `${wlsApiRoot(brand.organization.id)}/id-photos`,
    { headers: wlsApiHeaders(), params: { id: photoId, booking_code: bookingCode } }
  );

// ---------------------------------------------------------------------------
// Driver verification
// ---------------------------------------------------------------------------

export const postWlsDriverVerification = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/driver-verification`,
    data,
    { headers: wlsApiHeaders() }
  );

// ---------------------------------------------------------------------------
// Find listing / unit pricing / coupon codes
// ---------------------------------------------------------------------------

export const fetchWlsFindListing = (brand, brandId, unitId) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/find-listing`,
    { headers: wlsApiHeaders(), params: { brand_id: brandId, unit_id: unitId } }
  );

export const fetchWlsUnitPricing = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/unit-pricing`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

export const fetchWlsCouponCodes = (brand, listingId) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/coupon-codes`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId } }
  );

// ---------------------------------------------------------------------------
// Room availability / pricing
// ---------------------------------------------------------------------------

export const fetchWlsRoomAvailability = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/room/availability`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

export const fetchWlsRoomPricing = (brand, listingId, params) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/room/pricing`,
    { headers: wlsApiHeaders(), params: { listing_id: listingId, ...params } }
  );

// ---------------------------------------------------------------------------
// Booking calendar / events search
// ---------------------------------------------------------------------------

export const fetchWlsBookingCalendar = (brand, unitId) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/booking-calendar`,
    { headers: wlsApiHeaders(), params: { unit_id: unitId } }
  );

export const fetchWlsEventsSearch = (brand, query) =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/events-search`,
    {
      headers: wlsApiHeaders(),
      params: { query, organization_id: brand.organization.id }
    }
  );

// ---------------------------------------------------------------------------
// Conversations / Contact
// ---------------------------------------------------------------------------

export const postWlsConversation = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/conversations`,
    data,
    { headers: wlsApiHeaders() }
  );

// ---------------------------------------------------------------------------
// Auth config (authenticate integration)
// ---------------------------------------------------------------------------

export const reportWlsAuthConfigError = error => {
  console.error(
    'Failed to load WLS Authenticate configuration; verification remains disabled.',
    error
  );
  Honeybadger.notify(error);
};

export const fetchWlsAuthConfig = brand =>
  axios.get(
    `${wlsApiRoot(brand.organization.id)}/auth-config`,
    { headers: wlsApiHeaders(), params: { organization_id: brand.organization.id } }
  );

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------

export const postWlsReview = (brand, data) =>
  axios.post(
    `${wlsApiRoot(brand.organization.id)}/reviews`,
    data,
    { headers: wlsApiHeaders() }
  );
